import { useEffect, useState } from "react";
import { api } from "./api.js";
import { useGame } from "./game.js";

/**
 * Session status bar: is the DM in voice, who is "Host for tonight", and the claim / take-over buttons.
 * The DM only speaks while tonight's host (a Host or Co-host) is in the voice channel.
 */
export function LiveBanner() {
  const { session, campaign, user, isHost, isOwner, role, setSession, fail, playSession } = useGame();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 8000);
    return () => clearTimeout(t);
  }, [note]);
  if (!session) return null;
  const me = user.discordId;
  const holder = session.tonightHostId;
  const claimOpen = !!session.claimOpenUntil && Date.parse(session.claimOpenUntil) > Date.now();

  const claim = async () => {
    setBusy(true);
    try {
      const r = await api.claimTonight(campaign.id);
      setSession(r.live);
      setNote(r.message);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const takeOver = async () => {
    setBusy(true);
    try {
      setSession(await api.takeOver(campaign.id));
      setNote(null);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const canClaim = isHost && holder !== me && (!session.hostPresent || claimOpen);
  const canTakeOver = isOwner && role === "host" && holder !== null && holder !== me && session.hostPresent;

  if (!session.botInVoice) {
    return (
      <>
        <div className="live-banner idle">
          <span>
            The DM isn't in voice.{" "}
            {campaign.voiceChannelName
              ? `A Host or Co-host can type /join in the #${campaign.voiceChannelName} voice channel.`
              : "Link a Discord voice channel in Campaign settings, or type /join in a voice channel and pick this campaign."}
          </span>
        </div>
        <SessionBar />
      </>
    );
  }
  return (
    <>
    <div className={`live-banner ${session.active ? "active" : "waiting"}`}>
      <span className="dot live-dot" />
      {session.paused ? (
        <span>
          <strong>DM paused by the Host.</strong> The DM is listening in #{session.channelName} but won't speak or change the game until resumed.
        </span>
      ) : session.active ? (
        <span>
          Live in <strong>#{session.channelName}</strong> · Hosting tonight: <strong>{session.tonightHostName}</strong>
          {holder === me && " (you)"}
        </span>
      ) : (
        <span>
          <strong>Waiting for a host.</strong> The DM is listening quietly in #{session.channelName} until the Host or a Co-host is in voice
          {holder && session.tonightHostName ? ` (tonight: ${session.tonightHostName}, not in voice)` : ""}.
        </span>
      )}
      {note && <span className="muted small">{note}</span>}
      <span className="live-actions">
        {canClaim && (
          <button className="primary" disabled={busy} onClick={claim} title={claimOpen ? "Someone just claimed it: claiming now starts a d20 roll-off" : undefined}>
            {claimOpen && holder ? "Claim (d20 roll-off)" : "Host for tonight"}
          </button>
        )}
        {canTakeOver && (
          <button disabled={busy} onClick={takeOver}>
            Take over as host
          </button>
        )}
      </span>
    </div>
    <VoiceNotices />
    <SessionBar />
    {session.active && role && <TalkBox />}
    </>
  );
}

/**
 * Voice problems the bot noticed (a player's audio keeps breaking the decoder, a hot mic). The Host and Co-hosts see
 * every notice; a player sees the ones about them.
 */
export function VoiceNotices() {
  const { session, campaign, user, isHost, setSession, fail } = useGame();
  const notices = (session?.voiceNotices ?? []).filter((n) => isHost || n.userId === user.discordId);
  if (!notices.length) return null;
  const dismiss = (id: string) => api.dismissVoiceNotice(campaign.id, id).then(setSession, fail);
  return (
    <div className="voice-notices">
      {notices.map((n) => (
        <div key={n.id} className="live-banner waiting voice-notice">
          <span>
            <strong>{n.kind === "hotmic" ? "🎙️ Open mic" : "🔇 Audio trouble"}{n.userId === user.discordId ? " (you)" : `: ${n.name}`}.</strong> {n.text}
          </span>
          <span className="live-actions">
            <button onClick={() => dismiss(n.id)}>Dismiss</button>
          </span>
        </div>
      ))}
    </div>
  );
}


/** Tonight's play session: number, start time, Start / End Session (Host and Co-hosts). */
export function SessionBar() {
  const { campaign, isHost, playSession, setPlaySession, fail } = useGame();
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const start = () => run(async () => setPlaySession((await api.startSession(campaign.id)).session));
  const end = () =>
    run(async () => {
      if (!window.confirm("End tonight's session? The DM will write the summary and next time's \"Previously on…\" recap.")) return;
      await api.endSession(campaign.id);
      setPlaySession(null);
    });
  return (
    <div className="session-bar">
      {playSession ? (
        <span>
          <strong>Session {playSession.number}</strong> in progress since {new Date(playSession.startedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          {playSession.startedByName ? ` (started by ${playSession.startedByName})` : ""}
        </span>
      ) : (
        <span className="muted">No session running. {isHost ? "It starts when the DM joins voice, or press Start Session." : "Waiting for the Host to start the session."}</span>
      )}
      {isHost && (
        <span className="live-actions">
          {playSession ? (
            <button disabled={busy} onClick={end}>
              End Session
            </button>
          ) : (
            <button className="primary" disabled={busy} onClick={start}>
              Start Session
            </button>
          )}
        </span>
      )}
    </div>
  );
}

/** Text mode: type to the DM (answered aloud in voice), or "DM, go" to answer the latest table talk. */
export function TalkBox() {
  const { campaign, fail } = useGame();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => setSent(null), 5000);
    return () => clearTimeout(t);
  }, [sent]);
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.talkToDm(campaign.id, t);
      setText("");
      setSent("Sent. The DM will answer in voice.");
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const go = async () => {
    setBusy(true);
    try {
      await api.dmGo(campaign.id);
      setSent("The DM is answering the latest table talk.");
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="talk-box"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <input
        value={text}
        maxLength={1000}
        placeholder="Type to the DM (answered aloud in voice)…"
        onChange={(e) => setText(e.target.value)}
        disabled={busy}
      />
      <button type="submit" className="primary" disabled={busy || !text.trim()}>
        Send
      </button>
      <button type="button" disabled={busy} onClick={go} title="The DM responds to what was just said at the table (if it missed &quot;Dungeon Master&quot;)">
        DM, go
      </button>
      {sent && <span className="muted small">{sent}</span>}
    </form>
  );
}
